<template>
  <div class="pa-agreement-text">
    <h3 class="pa-agreement-text__title">{{ title }}</h3>
    <p class="pa-agreement-text__meta">
      Effective Date: {{ effectiveDateLabel }} &bull; Client: {{ clientName || 'the account owner' }}
      &bull; Website: {{ websiteLabel || 'your website' }}
    </p>
    <p>{{ preamble }}</p>
    <template v-for="section in sections">
      <h4 :key="section.title" class="pa-agreement-text__h">{{ section.title }}</h4>
      <template v-for="(block, i) in section.blocks">
        <ul v-if="block.ul" :key="section.title + '-ul-' + i" class="pa-agreement-text__list">
          <li v-for="item in block.ul" :key="item">{{ item }}</li>
        </ul>
        <p v-else :key="section.title + '-p-' + i" :class="{ 'pa-agreement-text__gap': block.gap }">
          <b v-if="block.lead" class="pa-agreement-text__lead">{{ block.lead }}</b>
          {{ block.p }}
        </p>
      </template>
    </template>
  </div>
</template>

<script>
import {
  AGREEMENT_PREAMBLE,
  AGREEMENT_SECTIONS,
  AGREEMENT_TITLE,
  agreementWebsiteLabel,
  formatAgreementDate,
} from '@/utils/popAuthorityAgreement'

/**
 * Full text of the Website Publishing Agreement (design pa-setup.jsx FullAgreement): scrollable box
 * under the "Read the full Website Publishing Agreement" link, in the setup wizard and on the
 * packages page. The header names the client, the website and the effective date (the day it was
 * signed, or today while unsigned).
 */
export default {
  name: 'PopAuthorityPublishingAgreementText',
  props: {
    clientName: { type: String, default: '' },
    websiteUrl: { type: String, default: '' },
    /** Backend timestamp or ISO string of the signature; today when empty. */
    effectiveDate: { type: String, default: '' },
  },
  data () {
    return {
      title: AGREEMENT_TITLE,
      preamble: AGREEMENT_PREAMBLE,
      sections: AGREEMENT_SECTIONS,
    }
  },
  computed: {
    effectiveDateLabel () {
      return formatAgreementDate(this.effectiveDate) || formatAgreementDate(new Date())
    },
    websiteLabel () {
      return agreementWebsiteLabel(this.websiteUrl)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';

/* design FullAgreement: 340px scroll box, white, 1px soft border, 8px radius, .84rem muted / 1.55 */
.pa-agreement-text {
  max-height: 340px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid $pa-border-soft;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 10px;
  font-size: 0.84rem;
  line-height: 1.55;
  color: $pa-muted;

  p,
  li {
    font-size: 0.84rem;
    line-height: 1.55;
    color: $pa-muted;
  }

  p {
    margin: 0;
  }

  &__title {
    margin: 0 0 6px;
    font-size: 1rem;
    line-height: 1.45;
    font-weight: 800;
    color: $pa-teal;
  }

  /* `p.` on purpose: these must outrank the `p { margin: 0 }` rule above */
  p.pa-agreement-text__meta {
    margin: 0 0 8px;
  }

  p.pa-agreement-text__gap {
    margin: 0 0 6px;
  }

  &__h {
    margin: 14px 0 6px;
    font-size: 0.92rem;
    line-height: 1.45;
    font-weight: 800;
    color: inherit;
  }

  /* design: the list sits inside .pa-tc, whose list rules give .88rem items 4px apart */
  &__list {
    margin: 0 0 6px;
    padding-left: 18px;
    list-style: disc;

    li {
      font-size: 0.88rem;
      margin-bottom: 4px;
    }
  }

  &__lead {
    color: $pa-teal;
  }
}
</style>
